import useRouterPath from '@appmaker/core/hooks/useRouterPath';
import appmaker from '@appmaker/core/index';
import { Link } from '@appmaker/core/routes';
import { ArrowLeftIcon, DotsVerticalIcon } from '@heroicons/react/outline';
import DropDownMenu from '@appmaker/components/DropDownMenu';
import { useExtensionDetail } from '../store/details';
import UnSavedChange from './UnSaved';
import { UPDATE_SETTINGS, UNINSTALL_EXTENSION } from '../api/graphql';
import { useMutation } from '@apollo/client';
import { useParams, useHistory } from '@appmaker/core/routes';
import { toast } from '@appmaker/components/ReactHotToastIntegrator';
import LoadingIcon from '@appmaker/components/LoadingIcon';
import { useState } from 'react';
import { hasExtensionSettingsSync, runExtensionSettingsOperation, syncExtensionSettings } from '../settingsSync';

const DropDownMenuWrapper = ({
	status,
	onClickDeactivate = () => {},
	onClickActivate = () => {},
	isLoadingDeactivate = false,
	isUnInstalling = false,
	onClickUninstall = () => {},
	onClickVersion = () => {},
	onClickDetails = () => {},
	onClickBackendSettings = () => {},
}) => {
	const { route } = useParams();
	let disabled = isLoadingDeactivate || isUnInstalling;
	const options = [];

	if (status === 'active') {
		options.push({
			label: 'Deactivate',
			onClick: onClickDeactivate,
			loading: isLoadingDeactivate,
			isLoadingText: 'Deactivating....',
			disabled,
		});
	} else {
		options.push({
			label: 'Activate',
			loading: isLoadingDeactivate,
			isLoadingText: 'Activating...',
			onClick: onClickActivate,
			disabled,
		});
	}
	options.push({
		label: <span className="text-red-600">Uninstall</span>,
		onClick: onClickUninstall,
		loading: isUnInstalling,
		isLoadingText: 'Uninstalling....',
		disabled,
	});
	if (route !== 'version') {
		options.push({
			label: 'Version',
			onClick: onClickVersion,
			loading: false,
			isLoadingText: 'Loading...',
			disabled,
		});
	} else {
		options.push({
			label: 'Details',
			onClick: onClickDetails,
			loading: false,
			isLoadingText: 'Loading...',
			disabled,
		});
	}
	if(route !== 'backend-settings'){
		options.push({
			label: 'Backend settings',
			onClick: onClickBackendSettings,
			loading: false,
			isLoadingText: 'Loading...',
			disabled,
		});
	}else{
		options.push({
			label: 'Settings',
			onClick: onClickDetails,
			loading: false,
			isLoadingText: 'Loading...',
			disabled,
		});
	}
	
	return (
		<DropDownMenu
			button={
				<span className="p-1.5 bg-gray-200 hover:bg-gray-100 rounded-md text-gray-600 self-center inline-flex items-center space-x-1">
					<DotsVerticalIcon className="h-6 w-6 transform 2xl:rotate-90" />
					<span className="text-sm hidden 2xl:block font-medium">OPTIONS</span>
				</span>
			}
			options={options}
		/>
	);
};

export default function DetailHeader(props) {
	const { handle, projectId } = useParams();
	const [syncingSettings, setSyncingSettings] = useState(false);
	const [pendingRecovery, setPendingRecovery] = useState(null);
	const getRouterPath = useRouterPath();
	const history = useHistory();
	const extension = useExtensionDetail((state) => state.extension);
	const syncActiveStatus = useExtensionDetail(
		(state) => state.syncActiveStatus
	);
	const syncUninstall = useExtensionDetail((state) => state.syncUninstall);
	const extensionInstalls = useExtensionDetail(
		(state) => state.extensionInstalls
	);

	const [updateSettings, { loading }] = useMutation(UPDATE_SETTINGS, {
		context: {
			headers: {
				'x-appmaker-project-id': projectId,
			},
		},
	});
	const [unInstall, { loading: unInstalling }] = useMutation(
		UNINSTALL_EXTENSION,
		{
			context: {
				headers: {
					'x-appmaker-project-id': projectId,
				},
			},
		}
	);
	const {
		extensionId,
		status,
		imageSrc,
		name: extensionName,
		price,
		id: extensionParamsId,
		icon,
	} = extension || {};
	const currentExtension = extensionInstalls?.find(
		(details) => details?.extension?.id === extension?.id
	);

	/**
	 * Attach to header using filter
	 */
	const overRideHeaderInstalled = appmaker.applyFilters(
		`${extensionParamsId}-override-header-component-installed`,
		null,
		{
			extension: props.extension || {},
		}
	);
	// Plugins are activated by handle (see get-plugins.js), so also allow
	// extensions to register their header override by handle. Falls back to the
	// id-based override above for backwards compatibility.
	const overRideHeaderByHandle = appmaker.applyFilters(
		`${extension?.handle}-override-header-component-installed`,
		null,
		{
			extension: props.extension || extension || {},
		}
	);
	const extensionHandle = extension?.handle || handle;
	const runOperation = (operation, messages) => {
		if (syncingSettings || loading || unInstalling) return;
		setSyncingSettings(true);
		toast.promise(
			runExtensionSettingsOperation(projectId, extensionHandle, operation)
				.finally(() => setSyncingSettings(false)),
			{ ...messages, error: error => <b>{error?.message || 'Unable to complete this action'}</b> }
		);
	};
	const changeStatus = nextStatus => {
		const activated = nextStatus === 'active';
		const failureMessage = `Extension ${activated ? 'activated' : 'deactivated'} in Appmaker, but sync failed. Use Retry sync below.`;
		runOperation(async () => {
			await updateSettings({ variables: {
				where: { id: currentExtension?.id }, data: { status: nextStatus },
			} });
			syncActiveStatus(currentExtension?.id, nextStatus);
			try {
				await syncExtensionSettings(projectId, extensionHandle, { failureMessage });
				setPendingRecovery(null);
			} catch (error) {
				setPendingRecovery({ kind: 'sync', message: error.message });
				throw error;
			}
		}, {
			loading: activated ? 'Activating...' : 'Deactivating...',
			success: <b>{activated ? 'Extension Activated' : 'Extension deactivated'}</b>,
		});
	};
	const onClickActivate = () => changeStatus('active');
	const onClickDeactivate = () => changeStatus('in_active');

	const retryStatusSync = () => runOperation(async () => {
		const failureMessage = 'Extension status is saved in Appmaker, but sync failed. Use Retry sync below.';
		try {
			await syncExtensionSettings(projectId, extensionHandle, { failureMessage });
			setPendingRecovery(null);
		} catch (error) {
			setPendingRecovery({ kind: 'sync', message: error.message });
			throw error;
		}
	}, { loading: 'Syncing extension...', success: <b>Extension synced</b> });

	const onClickUninstall = (retry = false) => {
		if (!retry && !window.confirm('Are you sure?')) return;
		runOperation(async () => {
			const requiresSync = hasExtensionSettingsSync(extensionHandle);
			if (requiresSync) {
				// The bridge reads the saved installation. Keep it until an inactive
				// snapshot is acknowledged so a failed sync can be safely retried.
				await updateSettings({ variables: {
					where: { id: currentExtension?.id }, data: { status: 'in_active' },
				} });
				syncActiveStatus(currentExtension?.id, 'in_active');
				const failureMessage = 'Extension deactivated in Appmaker, but sync failed. Uninstall is paused. Use Retry uninstall below.';
				try {
					await syncExtensionSettings(projectId, extensionHandle, { failureMessage });
				} catch (error) {
					setPendingRecovery({ kind: 'uninstall', message: error.message });
					throw error;
				}
			}
			try {
				await unInstall({ variables: { where: { id: currentExtension?.id } } });
			} catch (error) {
				const message = requiresSync
					? 'Extension is inactive and synced, but uninstall failed. Use Retry uninstall below.'
					: 'Unable to uninstall';
				setPendingRecovery({ kind: 'uninstall', message });
				throw new Error(message);
			}
			setPendingRecovery(null);
			syncUninstall(currentExtension?.id);
			history.push(getRouterPath('/extension'));
		}, { loading: 'Uninstalling...', success: <b>Extension Uninstalled</b> });
	};

	const onClickVersion = () => {
		history.push(getRouterPath(`/extension/${handle}/version`));
	};
	const onClickDetails = () => {
		history.push(getRouterPath(`/extension/${handle}/details`));
	};
	const onClickBackendSettings = () => {
		history.push(getRouterPath(`/extension/${handle}/backend-settings`));
	};
	const separateConfiguration = extension?.handle === 'back-in-stock-by-appmaker' || extensionParamsId === 'back-in-stock-by-appmaker';
	const installedHeader = overRideHeaderInstalled || overRideHeaderByHandle || null;
	return (
		<>
		<div className="flex items-center justify-between space-x-3 bg-white shadow-sm px-4 py-2 sticky top-0 z-50">
			<div className="flex items-center space-x-3">
				<Link to={getRouterPath('/extension/')}>
					<ArrowLeftIcon className="h-8 w-8 p-2 hover:bg-gray-300 rounded-md text-gray-600" />
				</Link>
				<div className="aspect-square rounded-lg overflow-hidden bg-gray-100 h-10 w-10">
					<img
						src={
							icon ||
							'https://storage.googleapis.com/appilder_cdn/extension-default.png'
						}
						alt={`${extensionName}.`}
						className="object-center object-contain h-10 w-10"
					/>
				</div>
				<div>
					<h4 className="text-lg font-semibold text-gray-900">
						{extensionName}
					</h4>
				</div>
			</div>

			<div className="flex items-center space-x-2">
				<UnSavedChange />
				{currentExtension?.status === 'in_active' && (
					<button
						type="button"
						onClick={onClickActivate}
						disabled={loading || syncingSettings}
						className="inline-flex justify-self-end bg-green-600 border border-transparent rounded-md py-1 px-3 items-center justify-center text-base font-normal text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-green-500"
					>
						{loading ? (
							<>
								<LoadingIcon
									className="-ml-0.5 h-6 w-6 animate-spin"
									aria-hidden="true"
								/>
								Activating...
							</>
						) : (
							'Activate Extension'
						)}
					</button>
				)}
				{!separateConfiguration && installedHeader}
				<DropDownMenuWrapper
					isLoadingDeactivate={loading || syncingSettings}
					isUnInstalling={unInstalling}
					status={currentExtension?.status}
					onClickDeactivate={onClickDeactivate}
					onClickActivate={onClickActivate}
					onClickUninstall={() => onClickUninstall()}
					onClickVersion={onClickVersion}
					onClickDetails={onClickDetails}
					onClickBackendSettings={onClickBackendSettings}
				/>
			</div>
		</div>
		{pendingRecovery && <div role="alert" className="max-w-3xl mx-auto mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">
			<p>{pendingRecovery.message}</p>
			<button type="button" disabled={syncingSettings || loading || unInstalling}
				onClick={pendingRecovery.kind === 'uninstall' ? () => onClickUninstall(true) : retryStatusSync}
				className="mt-2 font-medium underline disabled:opacity-50">
				{pendingRecovery.kind === 'uninstall' ? 'Retry uninstall' : 'Retry sync'}
			</button>
		</div>}
		{separateConfiguration && installedHeader && <div className="max-w-3xl mx-auto mt-4">{installedHeader}</div>}
		</>
	);
}
